import {useEffect, useState, useCallback,  useMemo} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {useNavigate, useLocation} from 'react-router-dom';
import {
  Statistic,
  Student,
  User,
} from '../../graphql/API';
import {reducersState} from '../../redux/reducers';
import { setSelectedClass, setClassStudents } from '../../redux/actions/class';

// @ts-ignore: TS2459 — we know it's there at runtime
import {
  ClassReportStatsBar,
  StudentsGridSection,
  MistakesSection,
} from '@PikaDoApp/pikado-web-dsm';
import SimpleLoader from '../../components/SimpleLoader/SimpleLoader';
import {ReportHeader} from '../../components/ReportHeader';
import {ReportSidebar} from '../../components/ReportSidebar';
import {ReportFooter} from '../../components/ReportFooter';

import {ClassMonthlyReportServiceWrapper} from './ClassReport/ClassMonthlyReport.service';
import {AttendanceChartContainer} from './AttendanceChartContainer';
import {DailyPerformanceContainer} from './DailyPerformanceContainer';
import {PerformanceChartsContainer} from './PerformanceChartsContainer';

import {
  transformStudentsMonthlyReportsForTable,
} from './classReportCalculations/studentTableHelpers';
import {ClassNextPrecExercises} from '../ClassPrevNextExe';

// Stub components for missing DSM components
const NoReportAvailable: React.FC = () => (
  <div className="p-8 text-center text-gray-500">
    <p>No report available yet. Please generate a report to see data.</p>
  </div>
);

const Loading: React.FC<{title?: string}> = ({title}) => (
  <SimpleLoader isLoading={true} title={title || 'Loading...'} />
);

const classReportService = new ClassMonthlyReportServiceWrapper();

type ReportPageType = {
  classId: string;
  selectedMonth: string;
  monthIndex: number;
  activeTab?: 'report' | 'exercises';
  onTabChange?: (tab: 'report' | 'exercises') => void;
};

export const ReportContent = ({
  classId,
  selectedMonth,
  activeTab = 'report',
  onTabChange,
}: ReportPageType) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //@ts-ignore
  const user: User = useSelector((state: any) => state.user.user);

  const school = useSelector((state: reducersState) => state.user?.school);
  const classes = useSelector((state: reducersState) => state.user?.classes);
  // Read selected class from Redux (ClassControl updates this)
  const selectedClassIdFromRedux = useSelector((state: reducersState) => state.classState?.selectedClassId);
  // Use Redux value if available, otherwise fall back to prop
  const activeClassId = selectedClassIdFromRedux || classId;

  // Sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(true);

  const [report, setReport] = useState<Statistic>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sortedStudents, setSortedStudents] = useState<Array<Student>>();
  const [classStatisticsData, setClassStatisticsData] = useState<any>(null);
  const [selectedMonthLocal, setSelectedMonthLocal] = useState<string>(selectedMonth);
  const [monthOptions, setMonthOptions] = useState<Array<{label: string; value: string}>>([]);
  const [classMonthlyReport, setClassMonthlyReport] = useState<any>()

  // Transform pastMonthlyAverageList to rawClassReports format
  const rawClassReports = useMemo(() => {
    if (!classStatisticsData?.pastMonthlyAverageList) return [];

    return classStatisticsData.pastMonthlyAverageList.map((item: { average: number; month: string }) => ({
      month: item.month,
      classMonthlyAverage: item.average,
      // Add any other properties needed by the existing logic
      monthlyReports: [], // Empty for now, populated by classMonthlyReport when selected
    }));
  }, [classStatisticsData?.pastMonthlyAverageList]);


  const studentMonthlyAverages = useMemo(() => {
    const averagesMap = new Map<string, number>();

    // Use the fetched classMonthlyReport for the selected month
    if (!classMonthlyReport || !classMonthlyReport.monthlyReports) {
      return averagesMap;
    }

    // Extract student averages from monthly reports
    classMonthlyReport.monthlyReports.forEach((studentReport: any) => {
      const studentId = studentReport.monthlyReportId?.studentId;
      const average = studentReport.monthlyReportId?.monthlyPerformance;

      if (studentId) {
        averagesMap.set(studentId, average ?? 0);
      }
    });

    return averagesMap;
  }, [classMonthlyReport]);

  // Generate month options from rawClassReports
  const generateMonthOptions = useMemo(() => {
    if (!rawClassReports || rawClassReports.length === 0) return [];

    return rawClassReports
      .map((report: any) => ({
        label: moment(report.month).format('MMMM YYYY'),
        value: moment(report.month).format('YYYY-MM'),
      }))
      .sort((a, b) => moment(a.value).diff(moment(b.value)));
  }, [rawClassReports]);

  // Sync month options to state
  useEffect(() => {
    setMonthOptions(generateMonthOptions);
  }, [generateMonthOptions]);


  // Fetch class statistics by classId
  useEffect(() => {
    if (!activeClassId) {
      setClassStatisticsData(null);
      return;
    }
    const fetchSelectedMonthClassMonthlyReport = async()=>{
            try {
              setIsLoading(true)
            const classMonthlyReport = await classReportService.getClassMonthlyReportByClassIdAndMonth(activeClassId,selectedMonthLocal);
            if (classMonthlyReport) {
              setClassMonthlyReport(classMonthlyReport)
            } else {
              setClassMonthlyReport(classMonthlyReport);
            }
            setIsLoading(false)
          } catch (error) {
            console.error('❌ [Component] Error fetching class statistics data:', error);
            setClassMonthlyReport(null);
            setIsLoading(false)
          }
    }
    const fetchClassStatisticsData = async () => {
      try {
        const statistics = await classReportService.getClassStatisticsByClassId(activeClassId);
        if (statistics) {
          console.log('✅ [Component] Setting classStatisticsData with:', statistics);
          setClassStatisticsData(statistics);
        } else {
          console.warn('⚠️ [Component] No statistics data found for class:', activeClassId);
          setClassStatisticsData(null);
        }
      } catch (error) {
        console.error('❌ [Component] Error fetching class statistics data:', error);
        setClassStatisticsData(null);
      }
    };
    fetchSelectedMonthClassMonthlyReport()
    fetchClassStatisticsData();
  }, [activeClassId,selectedMonthLocal]);



  // Month change handler
  const handleMonthChange = useCallback((newMonth: string) => {
    setSelectedMonthLocal(newMonth);
  }, []);

  // Prepare students data for StudentsGridSection
  const studentsData = useMemo(() => {
    if (classMonthlyReport?.studentsMonthlyReports) {
      // Use new helper to transform studentsMonthlyReports directly
      return transformStudentsMonthlyReportsForTable(classMonthlyReport.studentsMonthlyReports);
    }
  }, [classMonthlyReport, sortedStudents, studentMonthlyAverages]);

  // Store students data in Redux when available
  useEffect(() => {
    if (studentsData && studentsData.length > 0 && activeClassId) {
      dispatch(setClassStudents({
        classId: activeClassId,
        students: studentsData,
      }));
    }
  }, [studentsData, activeClassId, dispatch]);

  return (
    <>
      {isLoading ? (
        <Loading title="Analysing your data. it may take a minute." />
      ) : (
        <div className="flex h-screen bg-pikadoWhiteSmoke overflow-hidden">
          {/* Sidebar */}
          <ReportSidebar
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={() => setIsMobileSidebarOpen(false)}
            isCollapsed={isDesktopSidebarCollapsed}
            onToggleCollapse={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
          />

          {/* Main content area */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Sticky header + stats bar */}
            <div className="sticky top-0 z-30">
              <ReportHeader
                school={school}
                schoolName={school?.name || ''}
                studentCount={0}//TODO:
                userName={user?.firstName || ''}
                onMobileMenuClick={() => setIsMobileSidebarOpen(true)}
              />

              <ClassReportStatsBar
                activeTab={activeTab}
                onTabChange={(tab) => {
                  onTabChange?.(tab);
                }}
                classes={classes || []}
                selectedClassId={activeClassId}
                onClassChange={(classId) => {
                  dispatch(setSelectedClass(classId));
                  const schoolRoute = school?.id ? `/school/${school.id}/detailed` : '/';
                  navigate(schoolRoute);
                }}
                currentReport={null}
                studentCount={classMonthlyReport?.studentsCountInClassThisMonth}
                classLifeTimeAverage={classStatisticsData?.classLifeTimeAverage?.toFixed(2)}
              />
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {/* Report Tab Content */}
              {activeTab === 'report' && (
                <main className="flex flex-col gap-8 p-4 md:p-6 lg:p-8">
                  {!classMonthlyReport && <NoReportAvailable />}

                  {/* Attendance Chart */}
                  <AttendanceChartContainer
                    selectedMonth={selectedMonthLocal}
                    onMonthChange={handleMonthChange}
                    monthOptions={monthOptions}
                    studentsDailyRecord={classMonthlyReport?.studentsDailyRecord ||  []}
                    totalAnswersCount={classMonthlyReport?.totalClassAnswers}
                    totalStudentsEngagedThisMonth={classMonthlyReport?.totalStudentsEngagedThisMonth}
                  />

                  {/* Daily Performance */}
                  <DailyPerformanceContainer
                  studentsDailyAveragesInClassThisMonth={classMonthlyReport?.studentsDailyAveragesInClassThisMonth}
                    selectedMonth={selectedMonthLocal}
                    onMonthChange={handleMonthChange}
                    monthOptions={monthOptions}
                  />

                  {/* Performance Charts (Donut + Area) */}
                  <PerformanceChartsContainer
                    pastMonthlyAverageList={classStatisticsData?.pastMonthlyAverageList || []}
                    classMonthlyAverage={classMonthlyReport?.classMonthlyAverage.toFixed(2) || 0}
                    onMonthChange={handleMonthChange}
                    monthOptions={monthOptions}
                    selectedMonth={selectedMonthLocal}
                  />

                  {/* Students Grid */}
                  <StudentsGridSection
                    studentsData={studentsData}
                    onStudentClick={(studentId: string) => {
                      navigate(`/student/${studentId}`);
                    }}
                    onSeeAllClick={() => {
                      // Build cache key for the selected month and class
                      const cacheKey = `${activeClassId}-${selectedMonthLocal}`;
                      navigate('/students-arrangement', {
                        state: {
                          classId: activeClassId,
                          cacheKey: cacheKey,
                        },
                      });
                    }}
                  />

                  {/* Mistakes Section */}
                  <MistakesSection
                    syllables={classMonthlyReport?.classMonthlySyllables}
                    phonetics={classMonthlyReport?.classMonthlyPhonetics}
                  />

                  {/* Footers */}
                  <ReportFooter />
                </main>
              )}

              {/* Exercises Tab Content */}
              {activeTab === 'exercises' && activeClassId && (
                <main className="flex flex-col gap-8 p-4 md:p-6 lg:p-8">
                  <ClassNextPrecExercises selectedClassId={activeClassId} />
                </main>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};